import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Undo2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { rollbackCampaign } from '../../services/api';

// Remaining rollback window, e.g. "6 more days". Null when the window is closed.
export function rollbackRemaining(rollbackUntil, now = new Date()) {
  if (!rollbackUntil) return null;
  const ms = new Date(rollbackUntil) - now;
  if (!(ms > 0)) return null;
  const mins = Math.ceil(ms / 60000);
  if (mins < 60) return `${mins} more ${mins === 1 ? 'minute' : 'minutes'}`;
  if (mins < 2880) { const h = Math.round(mins / 60); return `${h} more ${h === 1 ? 'hour' : 'hours'}`; }
  const d = Math.round(mins / 1440);
  return `${d} more days`;
}

export default function RollbackDialog({ open, onOpenChange, campaign, onStarted }) {
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) setTyped(''); }, [open]);

  const matches = typed === campaign.channelName;

  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!matches || submitting) return;
    setSubmitting(true);
    try {
      const { runId } = await rollbackCampaign(campaign.id, typed);
      onOpenChange(false);
      onStarted(runId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start the rollback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="bg-[#18181B] border-[#27272A]" data-testid="rollback-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Roll back</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Aliases and the default menu go back to the menus that were live before this launch. The menus this campaign
              published are removed from LINE.
            </DialogDescription>
          </DialogHeader>
          <Label htmlFor="rollback-confirm-input" className="text-sm block mt-4">
            Type <span className="font-semibold text-white">{campaign.channelName}</span> to confirm
          </Label>
          <Input
            id="rollback-confirm-input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            className="mt-2 bg-[#09090B] border-[#27272A]"
            data-testid="rollback-confirm-input"
          />
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" className="border-[#27272A]" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
            <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white" disabled={!matches || submitting} data-testid="rollback-confirm">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Undo2 className="w-4 h-4 mr-2" />}
              Roll back
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
